import {
  Box,
  Dialog, DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles, TextField,
} from '@material-ui/core';
import {ButtonPurple, ButtonSmall} from 'components/Buttons';
import {IconClose, IconHeart} from 'components/Icons';
import Typo from 'components/Typo';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {feedbackPopupsSelector} from 'store/global/selectors';
import {rieltor} from 'styles/theme';
import {useSubmitFeedback} from './hooks';
import {FeedbackType} from './types';

interface FeedbackFormProps {
  type: FeedbackType;
  title?: string;
  titleIcon?: JSX.Element;
  question?: string;
  className?: string;
  style?: React.CSSProperties;
}

interface FeedbackFormDialogProps extends FeedbackFormProps {
  title: string;
  question: string;
  isOpened: boolean;
  onClose: () => void;
  minLength?: number;
  maxLength?: number;
}

const useFeedbackFormStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    columnGap: '10px',
    height: '30px',
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'space-between',
    },
  },
  popupWrapper: {
    '& .MuiDialog-paper': {
      minWidth: '550px',
      maxWidth: '800px !important',
      [theme.breakpoints.down('xs')]: {
        minWidth: 'auto',
        maxWidth: '100% !important',
        margin: '4px',
      },
    },
    '& .MuiDialogContent-root': {
      padding: '0px 30px 16px',
      [theme.breakpoints.down('xs')]: {
        padding: '0px 16px 16px',
      },
    },
    '& .MuiDialogTitle-root': {
      padding: '16px 16px 0px 16px',
    },
    '& .MuiFormHelperText-root': {
      margin: '4px 0 0 0',
    },
  },
  title: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '10px',
  },
  question: {
    fontFamily: rieltor.fontFamily,
    fontSize: '14px',
    padding: '16px 0',
  },
}));

const MIN_FEEDBACK_LENGTH = 20;
const MAX_FEEDBACK_LENGTH = 10_000; // Varchar length in user_feedbacks table
const DEFAULT_FEEDBACK_POPUP_TITLE = 'Як вам новий розділ?';
const DEFAULT_FEEDBACK_POPUP_SUBTITLE = 'Будь-ласка, напишіть, що вам подобається у цьому розділі? Що не подобається? Які ваші пропозиції по його покращенню? Будемо дуже вдячні за ваш відгук!';
const DEFAULT_ENABLED_FEEDBACK_TYPES = [
  FeedbackType.MarketActivity,
  FeedbackType.Rieltors,
  FeedbackType.Objects,
  FeedbackType.FopIbanReview,
];

const getConfiguredText = (value?: string | number | null, fallback?: string) => {
  const text = value === undefined || value === null ? '' : value.toString().trim();
  return text || fallback || '';
};

const isEnabledValue = (value: boolean | string | number | undefined, fallback: boolean) => {
  if (value === undefined) {
    return fallback;
  }

  return value === true || value === 1 || value === '1' || value === 'true';
};

export const FeedbackForm = ({
                               type,
                               title,
                               titleIcon,
                               question,
                               className,
                               style,
                             }: FeedbackFormProps) => {
  const classes = useFeedbackFormStyles();
  const feedbackPopupsConfig = useSelector(feedbackPopupsSelector);
  const feedbackPopupConfig = feedbackPopupsConfig?.[type];
  const [isOpened, setOpened] = useState(false);
  const isEnabled = isEnabledValue(
    feedbackPopupConfig?.enabled,
    DEFAULT_ENABLED_FEEDBACK_TYPES.includes(type),
  );
  const resolvedTitle = getConfiguredText(
    title,
    getConfiguredText(feedbackPopupConfig?.title, DEFAULT_FEEDBACK_POPUP_TITLE),
  );
  const resolvedQuestion = getConfiguredText(
    question,
    getConfiguredText(feedbackPopupConfig?.subtitle, DEFAULT_FEEDBACK_POPUP_SUBTITLE),
  );
  // const [message, setMessage] = useState('');
  // const messageFiltered = message.replace(/^\s+|\s+$/gm, '');

  // const [isLoading, submitFeedback] = useSubmitFeedback(
  //   () => {
  //     setOpened(false);
  //     setMessage('');
  //   }
  // );

  if (!isEnabled) {
    return null;
  }

  return (
    <div className={[classes.root, className].filter(Boolean).join(' ')} style={style}>
      <Typo variant={'text3'} style={{whiteSpace: 'nowrap'}}>
        {resolvedTitle}
      </Typo>

        {<ButtonSmall
          startIcon={<IconHeart/>}
          onClick={() => setOpened(true)}
        >
          Залишити відгук
        </ButtonSmall>}

        <FeedbackFormDialog
          type={type}
          title={resolvedTitle}
          titleIcon={titleIcon}
          question={resolvedQuestion}
          isOpened={isOpened}
          onClose={() => setOpened(false)}
        />
      </div>
  );
};

export const FeedbackFormDialog = ({
                                     type,
                                     title,
                                     titleIcon,
                                     question,
                                     isOpened,
                                      onClose,
                                      minLength = MIN_FEEDBACK_LENGTH,
                                      maxLength = MAX_FEEDBACK_LENGTH,
                                   }: FeedbackFormDialogProps) => {

  const classes = useFeedbackFormStyles();
  const [message, setMessage] = useState('');
  const messageFiltered = message.replace(/^\s+|\s+$/gm, '');

  const [isLoading, submitFeedback] = useSubmitFeedback(
      () => {
        onClose();
        setMessage('');
      }
  );


  return (<Dialog
      className={classes.popupWrapper}
      open={isOpened}
      onClose={() => onClose()}
  >
    <DialogTitle>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
        <div>&nbsp;</div>

        <IconClose
            style={{cursor: 'pointer', color: rieltor.gray4}}
            onClick={() => onClose()}
        />
      </Box>
    </DialogTitle>

    <DialogContent>
      <Typo variant={'title1'} className={classes.title}>
        {titleIcon}
        {title}
      </Typo>

      <div className={classes.question}>
        {question}
      </div>

      <TextField
          variant="outlined"
          fullWidth
          label={'Залишити відгук'}
          placeholder={'Залишити відгук'}
          required={true}
          disabled={isLoading}
          title={''}
          onChange={event => setMessage(event.target.value)}
          inputProps={{
            style: {
              minHeight: '120px',
              resize: 'vertical',
            },
          }}
          multiline={true}
          autoComplete="false"
          helperText={`Від ${minLength} до ${maxLength} символів`}
      />
    </DialogContent>

    <DialogActions style={{justifyContent: 'center', padding: '0 16px 20px 16px'}}>
      <ButtonPurple
          style={{minWidth: '150px'}}
          disabled={
              isLoading ||
              messageFiltered.length < minLength ||
              messageFiltered.length > maxLength
          }
          onClick={() => submitFeedback({
            type,
            message: messageFiltered,
          })}
      >
        Надіслати
      </ButtonPurple>
    </DialogActions>
  </Dialog>);
};
